import React from "react";
import { Link } from "react-router-dom";

const steps = [
  {
    number: "1",
    title: "Connectez votre LinkedIn",
    description:
      "Depuis le tableau de bord, cliquez sur « Connecter LinkedIn ». Une fenêtre s'ouvre pour vous authentifier sur LinkedIn via notre partenaire sécurisé. Une fois connecté, la fenêtre se ferme et votre compte apparaît en vert.",
    tip: "Votre mot de passe LinkedIn n'est jamais stocké sur nos serveurs.",
  },
  {
    number: "2",
    title: "Créez un segment",
    description:
      "Un segment représente une audience cible. Allez dans « Segments » puis « Nouveau segment ». Remplissez les champs :",
    details: [
      {
        label: "Nom",
        text: "Un nom court pour identifier le segment (ex : « CTOs SaaS France »).",
      },
      {
        label: "Cible",
        text: "Décrivez en langage naturel qui vous voulez contacter. L'IA transformera cette description en recherche LinkedIn.",
      },
      {
        label: "Instructions IA",
        text: "Expliquez comment l'IA doit se présenter et quel message envoyer. C'est le cœur de la personnalisation.",
      },
      {
        label: "Messages par jour",
        text: "Entre 1 et 3 messages par jour par segment. On recommande 2 pour rester naturel.",
      },
    ],
  },
  {
    number: "3",
    title: "Recherchez des contacts",
    description:
      "Sur la carte du segment, activez le bouton « Recherche auto ». Une première recherche LinkedIn part tout de suite, puis un cron se relance à 08h00 et 20h00 (heure de Paris) pour réalimenter le segment. Utilisez « Voir contacts » pour vérifier les profils trouvés et l’historique des recherches.",
    tip: "Vous pouvez relancer la recherche pour ajouter plus de contacts.",
  },
  {
    number: "4",
    title: "Activez le segment",
    description:
      "Vérifiez que le segment est bien en statut « Actif » (badge vert). Si ce n'est pas le cas, cliquez sur le badge pour l'activer. L'IA commencera alors à agir automatiquement selon le mode choisi.",
  },
  {
    number: "5",
    title: "L'IA travaille pour vous",
    description:
      "Chaque jour, dans la plage horaire choisie, l'IA sélectionne les contacts en attente, consulte leur profil LinkedIn, puis effectue l'action définie par le mode du segment : visite de profil, demande de connexion, invitation avec note personnalisée, ou message direct. Chaque action est adaptée au profil du destinataire.",
    tip: "Les actions sont espacées par des délais aléatoires pour imiter un comportement humain.",
  },
  {
    number: "6",
    title: "Suivez l'activité",
    description:
      "Le tableau de bord affiche en temps réel le nombre de messages envoyés, les segments actifs et le fil d'activité avec le détail de chaque message envoyé par l'IA. Reprenez la main sur LinkedIn à tout moment pour continuer les conversations initiées.",
  },
];

export default function GuidePage() {
  return (
    <div className="max-w-2xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-display font-bold text-ink tracking-tight">
          Guide d'utilisation
        </h1>
        <p className="text-mute mt-1">
          Tout ce qu'il faut savoir pour utiliser MmeValentine en quelques minutes.
        </p>
      </div>

      {/* Intro card */}
      <div className="card p-6">
        <h2 className="type-section-title text-ink mb-3">
          Comment ça marche ?
        </h2>
        <p className="text-sm text-ink/70 leading-relaxed">
          MmeValentine automatise votre prospection LinkedIn grâce à l'intelligence artificielle.
          Vous définissez des <strong>segments</strong> (vos audiences cibles) avec des instructions
          sur comment l'IA doit approcher chaque profil, puis choisissez un <strong>mode</strong> :
          visite de profil, demande de connexion, invitation avec note ou message direct (DM).
          L'IA agit ensuite automatiquement, chaque jour, directement depuis votre compte LinkedIn —
          chaque action est unique et adaptée au profil du destinataire.
        </p>
      </div>

      {/* Modes card */}
      <div className="card p-6">
        <h2 className="type-section-title text-ink mb-3">
          Les 4 modes d'activation
        </h2>
        <p className="text-sm text-ink/70 leading-relaxed mb-4">
          Chaque segment fonctionne dans l'un de ces quatre modes, choisi à sa création :
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            {
              name: "Visite",
              text: "Visite le profil du contact pour susciter la curiosité, sans message ni demande de connexion.",
            },
            {
              name: "Connexion",
              text: "Envoie une demande de connexion sans note, pour agrandir votre réseau.",
            },
            {
              name: "Invitation",
              text: "Envoie une demande de connexion avec une note personnalisée générée par l'IA.",
            },
            {
              name: "Message direct (DM)",
              text: "Envoie un ou plusieurs messages personnalisés aux contacts déjà dans votre réseau.",
            },
          ].map((mode) => (
            <div key={mode.name} className="rounded-lg border border-line p-3">
              <p className="text-sm font-semibold text-ink">{mode.name}</p>
              <p className="text-xs text-ink/70 mt-1">{mode.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-4">
        {steps.map((step) => (
          <div key={step.number} className="card p-6">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-rouge rounded-full flex items-center justify-center shadow-rouge">
                <span className="text-white font-display font-bold text-sm">
                  {step.number}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-semibold text-ink tracking-tight text-base">
                  {step.title}
                </h3>
                <p className="text-sm text-ink/70 mt-2 leading-relaxed">
                  {step.description}
                </p>

                {step.details && (
                  <ul className="mt-3 space-y-2">
                    {step.details.map((d) => (
                      <li key={d.label} className="text-sm">
                        <span className="font-semibold text-ink">{d.label} : </span>
                        <span className="text-ink/70">{d.text}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {step.tip && (
                  <div className="mt-3 px-3 py-2 bg-rouge-soft/50 rounded-lg border border-rouge/10">
                    <p className="text-xs text-rouge">
                      <span className="font-semibold">Conseil : </span>
                      {step.tip}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Example card */}
      <div className="card p-6">
        <h2 className="type-section-title text-ink mb-3">
          Exemple de segment
        </h2>
        <div className="space-y-3 text-sm">
          <div>
            <span className="type-label-accent">Nom</span>
            <p className="text-ink/70 mt-0.5">Directeurs Marketing E-commerce</p>
          </div>
          <div>
            <span className="type-label-accent">Cible</span>
            <p className="text-ink/70 mt-0.5">
              Directeurs marketing et CMO de sites e-commerce en France,
              entreprises de 20 à 500 employés, secteurs mode, beauté ou maison.
            </p>
          </div>
          <div>
            <span className="type-label-accent">Instructions IA</span>
            <p className="text-ink/70 mt-0.5">
              Tu es consultant en acquisition digitale. Mentionne un point spécifique
              du parcours de la personne (son entreprise actuelle ou un poste précédent).
              Propose un échange informel de 15 minutes sur les stratégies d'acquisition
              qui fonctionnent en ce moment dans le e-commerce. Sois concis et direct,
              comme un pair qui écrit à un autre professionnel.
            </p>
          </div>
          <div>
            <span className="type-label-accent">Messages par jour</span>
            <p className="text-ink/70 mt-0.5">2</p>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="text-center pb-8">
        <Link to="/segments/new" className="btn-primary inline-flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Créer mon premier segment
        </Link>
      </div>
    </div>
  );
}
